import {
  AlertCircle,
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Cpu,
  Database,
  Eye,
  GitBranch,
  Loader2,
  RotateCcw,
  Send,
  Sparkles,
  User,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, useToast } from "@/components/shared";
import { Markdown } from "@/components/shared/Markdown";
import { AskUserCard } from "@/components/tasks/AskUserCard";
import { featureFlags } from "@/config/featureFlags";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toAgentCreatePayload } from "@/lib/agentBuilderConfig";
import { getApiErrorMessage } from "@/lib/api";
import { agentService, builderService, flowService } from "@/lib/services";
import type {
  AgentBuilderResponse,
  AgentBuilderResult,
  AskUserQuestion,
  BuilderMessage,
  FlowBuilderResponse,
  FlowBuilderResult,
  FlowBuilderStepConfig,
  TaskUserInputResponse,
} from "@/types";

type BuilderMode = "agent" | "flow";

interface ChatMessage extends BuilderMessage {
  id: string;
  timestamp: Date;
  agentConfig?: AgentBuilderResult | null;
  flowConfig?: FlowBuilderResult | null;
  isComplete?: boolean;
  questions?: AskUserQuestion[] | null;
  /** Whether questions in this message have already been answered. */
  questionsAnswered?: boolean;
}

export function Builder() {
  const navigate = useNavigate();
  const { teams } = useApp();
  const { profile } = useAuth();
  const { showToast } = useToast();

  const defaultTeamUuid = teams.find((t) => t.isEveryoneTeam)?.uuid || teams[0]?.uuid || "";

  const [mode, setMode] = useState<BuilderMode>("agent");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedConfig, setExpandedConfig] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input on mount and mode change
  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const resetConversation = () => {
    setMessages([]);
    setInput("");
    setExpandedConfig(null);
  };

  const handleModeSwitch = (newMode: BuilderMode) => {
    if (newMode !== mode) {
      setMode(newMode);
      resetConversation();
    }
  };

  const sendMessageWithText = async (text: string) => {
    if (!text || isLoading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    // Build conversation history for API (exclude metadata)
    const currentMessages = [...messages, userMsg];
    const conversationHistory: BuilderMessage[] = currentMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      if (mode === "agent") {
        const response = await builderService.buildAgent({
          messages: conversationHistory,
          teamUuid: defaultTeamUuid,
        });

        const data: AgentBuilderResponse = response.data;
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: data.reply,
          timestamp: new Date(),
          agentConfig: data.agentConfig,
          isComplete: data.isComplete,
          questions: data.questions,
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (data.isComplete && data.agentConfig) {
          setExpandedConfig(assistantMsg.id);
        }
      } else {
        const response = await builderService.buildFlow({
          messages: conversationHistory,
          teamUuid: defaultTeamUuid,
        });

        const data: FlowBuilderResponse = response.data;
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: data.reply,
          timestamp: new Date(),
          flowConfig: data.flowConfig,
          isComplete: data.isComplete,
          questions: data.questions,
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (data.isComplete && data.flowConfig) {
          setExpandedConfig(assistantMsg.id);
        }
      }
    } catch (err) {
      console.error("Builder API error:", err);
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: getApiErrorMessage(err, "Sorry, something went wrong. Please try again."),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    await sendMessageWithText(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /**
   * Format structured question responses into a readable user message
   * and send it through the conversation.
   */
  const handleStructuredSubmit = useCallback(
    (responses: TaskUserInputResponse[], messageId: string) => {
      // Mark the questions in the originating message as answered
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, questionsAnswered: true } : m)));

      // Format responses into a readable string for the conversation
      const parts = responses.map((r) => {
        const selections = r.selected.join(", ");
        const custom = r.customText ? ` (${r.customText})` : "";
        return `**${r.header}**: ${selections}${custom}`;
      });
      const formattedResponse = parts.join("\n");

      sendMessageWithText(formattedResponse);
    },
    [messages, mode, isLoading],
  );

  /**
   * Handle legacy (free-text) submission from AskUserCard.
   */
  const handleLegacySubmit = useCallback(
    (text: string, messageId: string) => {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, questionsAnswered: true } : m)));
      sendMessageWithText(text);
    },
    [messages, mode, isLoading],
  );

  const createAgent = async (config: AgentBuilderResult) => {
    if (!defaultTeamUuid || isCreating) return;

    setIsCreating(true);
    try {
      const response = await agentService.create(toAgentCreatePayload(config, defaultTeamUuid));

      showToast(`Agent "${response.data.name}" created successfully!`, "success");
      navigate("/agents", { state: { newAgentUuid: response.data.uuid } });
    } catch (err) {
      console.error("Failed to create agent:", err);
      showToast(getApiErrorMessage(err, "Failed to create agent. Please try again."), "error");
    } finally {
      setIsCreating(false);
    }
  };

  const createFlow = async (config: FlowBuilderResult) => {
    if (!defaultTeamUuid || isCreating) return;

    setIsCreating(true);
    try {
      // First, create any new agents that the flow needs
      const stepAgentUuids: string[] = [];

      for (const step of config.steps) {
        if (step.agentUuid) {
          stepAgentUuids.push(step.agentUuid);
        } else if (step.newAgent) {
          // Create the new agent first
          const agentRes = await agentService.create(toAgentCreatePayload(step.newAgent, defaultTeamUuid));
          stepAgentUuids.push(agentRes.data.uuid);
        }
      }

      // Now create the flow
      const response = await flowService.create({
        name: config.name,
        description: config.description,
        visibility: config.visibility,
        team_uuid: defaultTeamUuid,
        steps: stepAgentUuids.map((agentUuid, i) => ({
          agent_uuid: agentUuid,
          config: config.steps[i]?.customPrompt ? { custom_prompt: config.steps[i].customPrompt } : {},
          requires_review: config.steps[i]?.requiresReview ?? false,
        })),
      });

      showToast(`Workflow "${response.data.name}" created successfully!`, "success");
      navigate(`/flows/${response.data.uuid}`);
    } catch (err) {
      console.error("Failed to create flow:", err);
      showToast(getApiErrorMessage(err, "Failed to create workflow. Please try again."), "error");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <PageHeader
        icon={Sparkles}
        title="AI Builder"
        subtitle="Describe what you want to build and AI will configure it for you"
        actions={
          messages.length > 0 ? (
            <button
              onClick={resetConversation}
              className="flex items-center gap-2 px-3 py-2 text-sm text-content-tertiary hover:text-content-primary border border-border-primary rounded-lg hover:bg-surface-secondary transition"
            >
              <RotateCcw className="w-4 h-4" />
              Start Over
            </button>
          ) : undefined
        }
      >
        {/* Mode toggle */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => handleModeSwitch("agent")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              mode === "agent"
                ? "bg-brand-gradient text-white shadow-md"
                : "text-content-tertiary hover:text-content-primary hover:bg-surface-secondary border border-border-primary"
            }`}
          >
            <Bot className="w-4 h-4" />
            Build an Agent
          </button>
          {featureFlags.workflows && (
            <button
              onClick={() => handleModeSwitch("flow")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                mode === "flow"
                  ? "bg-brand-gradient text-white shadow-md"
                  : "text-content-tertiary hover:text-content-primary hover:bg-surface-secondary border border-border-primary"
              }`}
            >
              <GitBranch className="w-4 h-4" />
              Build a Workflow
            </button>
          )}
        </div>
      </PageHeader>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto p-6">
        {messages.length === 0 ? (
          <EmptyState mode={mode} onSuggestionClick={setInput} />
        ) : (
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((msg, idx) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                displayName={profile?.user?.displayName || profile?.user?.name || "You"}
                expandedConfig={expandedConfig}
                onToggleConfig={(id) => setExpandedConfig((prev) => (prev === id ? null : id))}
                onCreateAgent={createAgent}
                onCreateFlow={createFlow}
                isCreating={isCreating}
                isLastAssistant={msg.role === "assistant" && idx === messages.length - 1}
                onStructuredSubmit={handleStructuredSubmit}
                onLegacySubmit={handleLegacySubmit}
                isLoading={isLoading}
              />
            ))}
            {isLoading && (
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-content-primary">AI Builder</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-border-primary px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  mode === "agent"
                    ? "Describe the agent you want to create..."
                    : "Describe the workflow you want to automate..."
                }
                rows={1}
                className="w-full px-4 py-3 bg-surface-secondary border border-border-primary rounded-lg text-content-primary placeholder-content-quaternary resize-none focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary transition"
                style={{ minHeight: "48px", maxHeight: "150px" }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${Math.min(target.scrollHeight, 150)}px`;
                }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="p-3 bg-brand-gradient text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shrink-0"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-content-quaternary mt-2 text-center">
            AI may make mistakes. Review the generated configuration before creating.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State component
// ---------------------------------------------------------------------------

function EmptyState({
  mode,
  onSuggestionClick,
}: Readonly<{
  mode: BuilderMode;
  onSuggestionClick: (text: string) => void;
}>) {
  const agentSuggestions = [
    "Create a customer support agent that can answer questions about our products",
    "Build a code review assistant that checks for best practices and security issues",
    "Make a content writer that creates blog posts in our brand voice",
    "Design a data analyst agent that can interpret charts and spreadsheets",
  ];

  const flowSuggestions = [
    "Create a research pipeline that gathers info, then summarizes, then writes a report",
    "Build a content approval workflow: draft, review, edit, and publish",
    "Design a customer onboarding process with multiple agent steps",
    "Set up a data processing pipeline that extracts, transforms, and loads data",
  ];

  const suggestions = mode === "agent" ? agentSuggestions : flowSuggestions;

  return (
    <div className="max-w-2xl mx-auto flex flex-col items-center justify-center h-full py-12">
      <div className="w-16 h-16 rounded-2xl bg-brand-gradient flex items-center justify-center mb-6">
        {mode === "agent" ? <Bot className="w-8 h-8 text-white" /> : <GitBranch className="w-8 h-8 text-white" />}
      </div>
      <h2 className="text-xl font-semibold text-content-primary mb-2">
        {mode === "agent" ? "What kind of agent do you want to build?" : "What workflow do you want to automate?"}
      </h2>
      <p className="text-sm text-content-tertiary mb-8 text-center max-w-md">
        {mode === "agent"
          ? "Describe the agent's purpose and I'll help you configure the name, system prompt, model, tools, and more."
          : "Describe the process you want to automate and I'll design a multi-step workflow with the right agents."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => onSuggestionClick(suggestion)}
            className="text-left px-4 py-3 bg-surface-secondary border border-border-primary rounded-lg text-sm text-content-secondary hover:bg-interactive-hover hover:text-content-primary hover:border-brand-primary/30 transition"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message Bubble component
// ---------------------------------------------------------------------------

function MessageBubble({
  message,
  displayName,
  expandedConfig,
  onToggleConfig,
  onCreateAgent,
  onCreateFlow,
  isCreating,
  onStructuredSubmit,
  onLegacySubmit,
  isLoading,
}: Readonly<{
  message: ChatMessage;
  displayName: string;
  expandedConfig: string | null;
  onToggleConfig: (id: string) => void;
  onCreateAgent: (config: AgentBuilderResult) => void;
  onCreateFlow: (config: FlowBuilderResult) => void;
  isCreating: boolean;
  isLastAssistant: boolean;
  onStructuredSubmit: (responses: TaskUserInputResponse[], messageId: string) => void;
  onLegacySubmit: (text: string, messageId: string) => void;
  isLoading: boolean;
}>) {
  const isUser = message.role === "user";
  const isExpanded = expandedConfig === message.id;

  return (
    <div className="flex gap-4">
      {/* Avatar */}
      {isUser ? (
        <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
      ) : (
        <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
          <Bot className="w-5 h-5 text-white" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Sender name + timestamp */}
        <div className="flex items-center gap-2 mb-2">
          <span className="font-semibold text-content-primary">{isUser ? displayName : "AI Builder"}</span>
          <span className="text-xs text-content-quaternary">{message.timestamp.toLocaleTimeString()}</span>
        </div>

        {/* Message content */}
        {isUser ? (
          <div className="text-content-secondary whitespace-pre-wrap wrap-break-word">{message.content}</div>
        ) : (
          <Markdown content={message.content} />
        )}

        {/* Agent config preview */}
        {message.agentConfig && message.isComplete && (
          <div className="mt-3">
            <button
              onClick={() => onToggleConfig(message.id)}
              className="flex items-center gap-2 text-sm text-brand-primary hover:text-brand-primary/80 transition mb-2"
            >
              <Eye className="w-4 h-4" />
              {isExpanded ? "Hide" : "Review"} Agent Configuration
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {isExpanded && (
              <AgentConfigCard
                config={message.agentConfig}
                onConfirm={() => onCreateAgent(message.agentConfig!)}
                isCreating={isCreating}
              />
            )}
          </div>
        )}

        {/* Flow config preview */}
        {message.flowConfig && message.isComplete && (
          <div className="mt-3">
            <button
              onClick={() => onToggleConfig(message.id)}
              className="flex items-center gap-2 text-sm text-brand-primary hover:text-brand-primary/80 transition mb-2"
            >
              <Eye className="w-4 h-4" />
              {isExpanded ? "Hide" : "Review"} Workflow Configuration
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {isExpanded && (
              <FlowConfigCard
                config={message.flowConfig}
                onConfirm={() => onCreateFlow(message.flowConfig!)}
                isCreating={isCreating}
              />
            )}
          </div>
        )}

        {/* Structured questions */}
        {!isUser && message.questions && message.questions.length > 0 && !message.questionsAnswered && (
          <div className="mt-3">
            <AskUserCard
              questions={message.questions}
              submitting={isLoading}
              onSubmitStructured={(responses) => onStructuredSubmit(responses, message.id)}
              onSubmitLegacy={(text) => onLegacySubmit(text, message.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent Config Card
// ---------------------------------------------------------------------------

export function AgentConfigCard({
  config,
  onConfirm,
  isCreating,
}: Readonly<{
  config: AgentBuilderResult;
  onConfirm: () => void;
  isCreating: boolean;
}>) {
  return (
    <div className="bg-surface-primary border border-border-primary rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-primary flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${config.color}20` }}
        >
          <Bot className="w-5 h-5" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-content-primary truncate">{config.name}</h3>
          {config.description && <p className="text-xs text-content-tertiary truncate">{config.description}</p>}
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded-full border"
          style={{
            borderColor: `${config.color}40`,
            color: config.color,
            backgroundColor: `${config.color}10`,
          }}
        >
          {config.visibility}
        </span>
      </div>

      {/* Details */}
      <div className="px-4 py-3 space-y-3">
        {/* Model */}
        <div className="flex items-center gap-2 text-sm">
          <Cpu className="w-4 h-4 text-content-quaternary shrink-0" />
          <span className="text-content-tertiary">Model:</span>
          <span className="text-content-primary font-medium">{config.modelId}</span>
        </div>

        {/* Tools */}
        {(config.toolConfigs?.length ?? 0) > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <Wrench className="w-4 h-4 text-content-quaternary shrink-0 mt-0.5" />
            <span className="text-content-tertiary">Tools:</span>
            <span className="text-content-primary">
              {config.toolConfigs.length} tool{config.toolConfigs.length === 1 ? "" : "s"} attached
            </span>
          </div>
        )}

        {/* Data Sources */}
        {(config.dataCollectionUuids?.length ?? 0) > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <Database className="w-4 h-4 text-content-quaternary shrink-0 mt-0.5" />
            <span className="text-content-tertiary">Data Sources:</span>
            <span className="text-content-primary">
              {config.dataCollectionUuids.length} source{config.dataCollectionUuids.length === 1 ? "" : "s"} attached
            </span>
          </div>
        )}

        {/* System Prompt preview */}
        <div className="text-sm">
          <span className="text-content-tertiary block mb-1">System Prompt:</span>
          <div className="bg-surface-secondary rounded-md px-3 py-2 text-xs text-content-secondary max-h-32 overflow-y-auto font-mono whitespace-pre-wrap">
            {config.systemPrompt}
          </div>
        </div>
      </div>

      {/* Action */}
      <div className="px-4 py-3 border-t border-border-primary">
        <button
          onClick={onConfirm}
          disabled={isCreating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creating Agent...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Create Agent
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flow Config Card
// ---------------------------------------------------------------------------

function FlowConfigCard({
  config,
  onConfirm,
  isCreating,
}: Readonly<{
  config: FlowBuilderResult;
  onConfirm: () => void;
  isCreating: boolean;
}>) {
  return (
    <div className="bg-surface-primary border border-border-primary rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-primary">
        <h3 className="font-semibold text-content-primary flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-brand-primary" />
          {config.name}
        </h3>
        {config.description && <p className="text-xs text-content-tertiary mt-1">{config.description}</p>}
      </div>

      {/* Steps */}
      <div className="px-4 py-3">
        <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">
          Steps ({config.steps.length})
        </span>
        <div className="mt-2 space-y-2">
          {config.steps.map((step, index) => (
            <FlowStepPreview key={`step-${step.agentUuid || step.newAgent?.name || index}`} step={step} index={index} />
          ))}
        </div>
      </div>

      {/* New agents warning */}
      {config.steps.some((s) => s.newAgent) && (
        <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/20">
          <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            New agents will be created automatically when you confirm.
          </div>
        </div>
      )}

      {/* Action */}
      <div className="px-4 py-3 border-t border-border-primary">
        <button
          onClick={onConfirm}
          disabled={isCreating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium text-sm"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creating Workflow...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Create Workflow
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flow Step Preview
// ---------------------------------------------------------------------------

function FlowStepPreview({
  step,
  index,
}: Readonly<{
  step: FlowBuilderStepConfig;
  index: number;
}>) {
  const isNewAgent = !!step.newAgent;
  const name = step.newAgent?.name || step.agentUuid || "Unknown";

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-surface-secondary rounded-lg">
      <div className="w-6 h-6 rounded-full bg-brand-primary/20 text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content-primary truncate">{name}</span>
          {isNewAgent && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium">
              NEW
            </span>
          )}
          {step.requiresReview && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">
              REVIEW
            </span>
          )}
        </div>
        {step.customPrompt && <p className="text-xs text-content-quaternary truncate mt-0.5">{step.customPrompt}</p>}
      </div>
      {isNewAgent ? (
        <Bot className="w-4 h-4 text-amber-500 shrink-0" />
      ) : (
        <Bot className="w-4 h-4 text-content-quaternary shrink-0" />
      )}
    </div>
  );
}
